// Solufy Industry Clients - Mobile Horizontal Auto-Scroll (2 seconds, 2-by-2 view)

export function mountIndustryClients(root = document) {
	const grids = root.querySelectorAll(".industry-clients-grid");
	if (!grids.length) return () => {};

	const cleanupFns = [];

	grids.forEach((grid) => {
		const cards = grid.querySelectorAll(".industry-client-item");
		if (cards.length <= 2) return;

		let intervalId = null;
		let resumeTimeout = null;
		let isUserInteracting = false;

		const isMobile = () => window.innerWidth <= 768;
		const prefersReducedMotion = () =>
			window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		const slideCount = Math.ceil(cards.length / 2);

		// Create pagination dots for mobile view
		const wrap = grid.closest(".industry-wrap") || grid.parentElement;
		let dotsWrap = wrap ? wrap.querySelector(".industry-clients-dots") : null;

		if (!dotsWrap && wrap && slideCount > 1) {
			dotsWrap = document.createElement("div");
			dotsWrap.className = "industry-clients-dots";
			dotsWrap.setAttribute("aria-hidden", "true");

			for (let i = 0; i < slideCount; i++) {
				const dot = document.createElement("button");
				dot.type = "button";
				dot.className = `industry-clients-dot ${i === 0 ? "is-active" : ""}`;
				dot.setAttribute("aria-label", `Client Slide ${i + 1}`);
				dot.addEventListener("click", () => {
					pauseAutoScroll();
					scrollToSlide(i);
					scheduleResume(4000);
				});
				dotsWrap.appendChild(dot);
			}
			wrap.appendChild(dotsWrap);
		}

		const updateActiveDot = () => {
			if (!dotsWrap) return;
			const maxScroll = Math.max(1, grid.scrollWidth - grid.clientWidth);
			const ratio = grid.scrollLeft / maxScroll;
			const activeIndex = Math.min(
				slideCount - 1,
				Math.max(0, Math.round(ratio * (slideCount - 1)))
			);
			const dots = dotsWrap.querySelectorAll(".industry-clients-dot");
			dots.forEach((dot, idx) => {
				dot.classList.toggle("is-active", idx === activeIndex);
			});
		};

		const scrollToSlide = (slideIndex) => {
			const maxScroll = grid.scrollWidth - grid.clientWidth;
			if (maxScroll <= 0) return;
			const targetCard = cards[Math.min(cards.length - 1, slideIndex * 2)];
			const targetLeft = targetCard
				? Math.min(maxScroll, Math.max(0, targetCard.offsetLeft - grid.offsetLeft))
				: Math.min(maxScroll, slideIndex * (grid.clientWidth + 12));
			grid.scrollTo({ left: targetLeft, behavior: "smooth" });
		};

		const nextStep = () => {
			if (!isMobile() || prefersReducedMotion() || isUserInteracting) return;
			const maxScroll = grid.scrollWidth - grid.clientWidth;
			if (maxScroll <= 4) return;

			const currentSlide = Math.min(
				slideCount - 1,
				Math.max(0, Math.round((grid.scrollLeft / maxScroll) * (slideCount - 1)))
			);
			const nextSlide = (currentSlide + 1) % slideCount;
			scrollToSlide(nextSlide);
		};

		const startAutoScroll = () => {
			stopAutoScroll();
			if (!isMobile() || prefersReducedMotion()) return;
			// Exactly 2 seconds auto-scroll interval as requested
			intervalId = setInterval(nextStep, 2000);
		};

		const stopAutoScroll = () => {
			if (intervalId) {
				clearInterval(intervalId);
				intervalId = null;
			}
		};

		const pauseAutoScroll = () => {
			stopAutoScroll();
			if (resumeTimeout) {
				clearTimeout(resumeTimeout);
				resumeTimeout = null;
			}
		};

		const scheduleResume = (delay = 3000) => {
			if (resumeTimeout) clearTimeout(resumeTimeout);
			resumeTimeout = setTimeout(() => {
				isUserInteracting = false;
				startAutoScroll();
			}, delay);
		};

		const onTouchStart = () => {
			isUserInteracting = true;
			pauseAutoScroll();
		};

		const onTouchEnd = () => {
			scheduleResume(3000);
		};

		const onScroll = () => {
			updateActiveDot();
		};

		const onResize = () => {
			if (isMobile()) {
				startAutoScroll();
			} else {
				stopAutoScroll();
				grid.scrollTo({ left: 0 });
			}
		};

		grid.addEventListener("touchstart", onTouchStart, { passive: true });
		grid.addEventListener("touchend", onTouchEnd, { passive: true });
		grid.addEventListener("touchcancel", onTouchEnd, { passive: true });
		grid.addEventListener("mouseenter", onTouchStart);
		grid.addEventListener("mouseleave", onTouchEnd);
		grid.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onResize, { passive: true });

		startAutoScroll();

		cleanupFns.push(() => {
			stopAutoScroll();
			if (resumeTimeout) clearTimeout(resumeTimeout);
			grid.removeEventListener("touchstart", onTouchStart);
			grid.removeEventListener("touchend", onTouchEnd);
			grid.removeEventListener("touchcancel", onTouchEnd);
			grid.removeEventListener("mouseenter", onTouchStart);
			grid.removeEventListener("mouseleave", onTouchEnd);
			grid.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onResize);
			if (dotsWrap && dotsWrap.parentElement) {
				dotsWrap.remove();
			}
		});
	});

	return () => {
		cleanupFns.forEach((fn) => fn());
	};
}

